import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $accountByIdStore, $profileFeatureStore } from '@lib/store';
import { useAccountFlow } from '@shared/hooks';
import type { AccountByIdResponse } from '@shared/api';
import { BaseInput, Button, ErrorMessage, Modal } from '@shared/widgets';

interface IForm {
  label: string;
  costCentre: string;
  category: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  account: AccountByIdResponse;
  initial: Partial<IForm>;
}

export const EditDetailsModal = (props: Props) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { control, formState, handleSubmit, reset } = useForm<IForm>({
    defaultValues: { label: props.initial.label || '', costCentre: props.initial.costCentre || '', category: props.initial.category || '' },
  });
  const { t } = useTranslation('account');

  const closeHandler = () => {
    props.onClose();
    reset({ label: props.initial.label || '', costCentre: props.initial.costCentre || '', category: props.initial.category || '' });
  };
  const submitHandler = async (data: IForm) => {
    await $accountByIdStore.events.updateDetails({
      accountId: props.account.id,
      label: data.label,
      costCentre: data.costCentre,
      category: data.category,
    });
    props.onClose();
  };

  const accountFlow = useAccountFlow(profileFeatureStore.data?.accountFlows, props.account.id);
  if (!accountFlow?.editAccountLabel && !accountFlow?.editCostCentre && !accountFlow?.editAccountCategory) return null;
  return (
    <Modal isOpen={props.show} onClose={closeHandler} title={t('sidebar.modals.editDetails.title')}>
      <form className='flex flex-col gap-5' noValidate onSubmit={handleSubmit(submitHandler)}>
        {accountFlow?.editAccountLabel && (
          <Controller
            name='label'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput
                  {...field}
                  label={t('sidebar.modals.editDetails.placeholders.accountLabel')}
                  placeholder={t('sidebar.modals.editDetails.placeholders.accountLabel')}
                  required
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {accountFlow?.editCostCentre && (
          <Controller
            name='costCentre'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput
                  {...field}
                  label={t('sidebar.modals.editDetails.placeholders.costCentre')}
                  placeholder={t('sidebar.modals.editDetails.placeholders.costCentre')}
                  required
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {accountFlow?.editAccountCategory && (
          <Controller
            name='category'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput
                  {...field}
                  label={t('sidebar.modals.editDetails.placeholders.accountCategory')}
                  placeholder={t('sidebar.modals.editDetails.placeholders.accountCategory')}
                  required
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        <Button className='mt-5' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('buttons.saveChanges', { ns: 'common' })}
        </Button>
      </form>
    </Modal>
  );
};
